import React from 'react';
import Select, { SingleValue } from 'react-select';
import makeAnimated from 'react-select/animated';
import { DatePickerField } from './DatePickerField';

const animatedComponents = makeAnimated();

export interface FilterOption {
  value: string;
  label: string;
}

export interface Filter {
  key: string;
  label: string;
  options: FilterOption[];
  currentValue: string | null;
}

export interface SortOption {
  value: string;
  label: string;
}

interface FilterSortPanelProps {
  children?: React.ReactNode;

  showSearch?: boolean;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  onSearchSubmit?: (value: string) => void;

  filters?: Filter[];
  onFilterChange?: (filterKey: string, value: string | null) => void;

  sortOptions?: SortOption[];
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSortByChange?: (value: string) => void;
  onSortOrderChange?: (value: 'asc' | 'desc') => void;

  showDateRange?: boolean;
  startDate?: Date | null;
  onStartDateChange?: (date: Date | null) => void;
  endDate?: Date | null;
  onEndDateChange?: (date: Date | null) => void;
  startDateLabel?: string;
  endDateLabel?: string;

  showSecondaryDateRange?: boolean;
  secondaryStartDate?: Date | null;
  onSecondaryStartDateChange?: (date: Date | null) => void;
  secondaryEndDate?: Date | null;
  onSecondaryEndDateChange?: (date: Date | null) => void;
  secondaryStartDateLabel?: string;
  secondaryEndDateLabel?: string;
}

const FilterSortPanel: React.FC<FilterSortPanelProps> = ({
  children,
  showSearch = true,
  searchTerm,
  onSearchChange,
  searchPlaceholder = 'Поиск...',
  onSearchSubmit,

  filters = [],
  onFilterChange,

  sortOptions = [],
  sortBy,
  sortOrder,
  onSortByChange,
  onSortOrderChange,

  showDateRange = false,
  startDate,
  onStartDateChange,
  endDate,
  onEndDateChange,
  startDateLabel = 'Дата с',
  endDateLabel = 'Дата по',

  showSecondaryDateRange = false,
  secondaryStartDate,
  onSecondaryStartDateChange,
  secondaryEndDate,
  onSecondaryEndDateChange,
  secondaryStartDateLabel = 'Второй диапазон с',
  secondaryEndDateLabel = 'Второй диапазон по',
}) => {
  const sortOrderOptions = [
    { value: 'asc' as const, label: 'По возрастанию' },
    { value: 'desc' as const, label: 'По убыванию' },
  ];

  return (
    <div className="px-6 py-4 border-b bg-gray-50 w-full rounded-t-lg">
      <div className="flex flex-wrap gap-4 items-end -mx-2">
        {/* Search */}
        {showSearch && (
          <div className="flex-1 min-w-[200px] px-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Поиск</label>
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    onSearchSubmit?.(searchTerm);
                  }
                }}
                placeholder={searchPlaceholder}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 pr-10 text-sm shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
              />
              <button
                type="button"
                onClick={() => onSearchSubmit?.(searchTerm)}
                aria-label="Найти"
                title="Искать"
                className="absolute inset-y-0 right-0 flex items-center px-2 rounded-r-lg"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  className="h-5 w-5 text-gray-500 transition-colors hover:text-gray-700"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.65 4.65a7.5 7.5 0 0011.97 11.97z"
                  />
                </svg>
              </button>
            </div>
          </div>
        )}

        {children && (
            <div className="flex-1 min-w-[200px] px-2">
                {children}
            </div>
        )}

        {/* Date Range 1 */}
        {showDateRange && (
          <>
            <div className="flex-1 min-w-[150px] px-2">
              <DatePickerField
                label={startDateLabel}
                selected={startDate || null}
                onChange={(date) => onStartDateChange?.(date)}
                maxDate={endDate || null}
              />
            </div>
            <div className="flex-1 min-w-[150px] px-2">
              <DatePickerField
                label={endDateLabel}
                selected={endDate || null}
                onChange={(date) => onEndDateChange?.(date)}
                minDate={startDate || null}
              />
            </div>
          </>
        )}

        {/* Date Range 2 */}
        {showSecondaryDateRange && (
          <>
            <div className="flex-1 min-w-[150px] px-2">
              <DatePickerField
                label={secondaryStartDateLabel}
                selected={secondaryStartDate || null}
                onChange={(date) => onSecondaryStartDateChange?.(date)}
                maxDate={secondaryEndDate || null}
              />
            </div>
            <div className="flex-1 min-w-[150px] px-2">
              <DatePickerField
                label={secondaryEndDateLabel}
                selected={secondaryEndDate || null}
                onChange={(date) => onSecondaryEndDateChange?.(date)}
                minDate={secondaryStartDate || null}
              />
            </div>
          </>
        )}

        {/* Filters */}
        {filters.map((filter) => {
          const options = filter.options.map(opt => ({ value: opt.value, label: opt.label }));
          const value =
            filter.currentValue !== null
              ? options.find(o => o.value === filter.currentValue) ||
                { value: filter.currentValue, label: filter.currentValue }
              : null;
          return (
            <div key={filter.key} className="flex-1 min-w-[180px] px-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">{filter.label}</label>
              <Select<{ value: string; label: string }>
                value={value}
                onChange={(newValue: SingleValue<{ value: string; label: string }>) => {
                  onFilterChange?.(filter.key, newValue?.value || null);
                }}
                options={options}
                isClearable
                placeholder="Выберите..."
                components={animatedComponents}
                className="text-sm"
                classNamePrefix="react-select"
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: '40px',
                    borderColor: '#d1d5db',
                    borderRadius: '0.5rem',
                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                    '&:hover': { borderColor: '#9ca3af' },
                  }),
                  menu: (base) => ({ ...base, zIndex: 50 }),
                }}
              />
            </div>
          );
        })}

        {/* Sort By (рендерится только если переданы sortOptions) */}
        {sortOptions.length > 0 && (
          <div className="flex-1 min-w-[180px] px-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Сортировать по</label>
            <Select<SortOption>
              value={sortOptions.find(opt => opt.value === sortBy) || null}
              onChange={(newValue) => newValue && onSortByChange?.(newValue.value)}
              options={sortOptions}
              isClearable={false}
              components={animatedComponents}
              className="text-sm"
              classNamePrefix="react-select"
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: '40px',
                  borderColor: '#d1d5db',
                  borderRadius: '0.5rem',
                  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                  '&:hover': { borderColor: '#9ca3af' },
                }),
                menu: (base) => ({ ...base, zIndex: 50 }),
              }}
            />
          </div>
        )}

        {/* Sort Order (рендерится только если переданы sortOptions) */}
        {sortOptions.length > 0 && (
          <div className="flex-1 min-w-[160px] px-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Порядок</label>
            <Select<{ value: 'asc' | 'desc'; label: string }>
              value={sortOrderOptions.find(opt => opt.value === sortOrder) || null}
              onChange={(newValue) => newValue && onSortOrderChange?.(newValue.value)}
              options={sortOrderOptions}
              isClearable={false}
              components={animatedComponents}
              className="text-sm"
              classNamePrefix="react-select"
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: '40px',
                  borderColor: '#d1d5db',
                  borderRadius: '0.5rem',
                  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                  '&:hover': { borderColor: '#9ca3af' },
                }),
                menu: (base) => ({ ...base, zIndex: 50 }),
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterSortPanel;